import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, TrendUp, TrendDown } from "@phosphor-icons/react";

const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';

/**
 * Small line shown under the live ELO number on the profile header.
 * Reveals the player's rolling baseline and the current tournament delta — so
 * members can see exactly how much the in-progress tournament is moving their
 * rating. Hidden when no baseline is set (e.g. external players, or pre-baseline
 * data).
 */
function EloBaselineLine({ eloRating, baseline }) {
  const delta = Math.round((eloRating - baseline) * 10) / 10;
  const up = delta > 0;
  const down = delta < 0;
  const Icon = up ? TrendUp : down ? TrendDown : null;
  const deltaColour = up ? "text-primary" : down ? "text-destructive" : "text-muted-foreground";
  return (
    <p
      className="text-[10px] text-muted-foreground mt-0.5 tabular-nums"
      title={`Rolling baseline ${baseline} · current tournament: ${up ? "+" : ""}${delta}`}
      data-testid="elo-baseline-line"
    >
      <span>base {baseline}</span>
      <span className="mx-1">·</span>
      <span className={`inline-flex items-center gap-0.5 font-semibold ${deltaColour}`} data-testid="elo-baseline-delta">
        {Icon && <Icon className="w-2.5 h-2.5" weight="bold" />}
        {up ? "+" : ""}{delta}
      </span>
    </p>
  );
}


const getEloColor = (elo) => {
  if (elo >= 1900) return "text-secondary";
  if (elo >= 1700) return "text-primary";
  if (elo >= 1500) return "text-chart-3";
  return "text-muted-foreground";
};

const getEloLabel = (elo) => {
  if (elo >= 2000) return "Elite";
  if (elo >= 1800) return "Expert";
  if (elo >= 1600) return "Advanced";
  if (elo >= 1400) return "Skilled";
  if (elo >= 1200) return "Intermediate";
  return "Novice";
};

const HEADER_BG_STYLE = {
  backgroundImage: `url(https://images.unsplash.com/photo-1714564209284-1ba07d735e76?w=1200&q=80)`,
};

export default function PlayerHeader({
  player, isAdmin, canUploadPhoto, uploading,
  fileInputRef, onPhotoUpload, onTierChange,
}) {
  const eloRating = player.elo_rating || 1500;
  return (
    <Card className="bg-card border-border overflow-hidden">
      <div className="h-32 bg-cover bg-center relative" style={HEADER_BG_STYLE}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-card" />
      </div>
      <CardContent className="relative -mt-16 pb-6">
        <div className="flex flex-col md:flex-row items-start md:items-end gap-6">
          <div className="relative">
            <Avatar className="h-28 w-28 border-4 border-card shadow-xl">
              <AvatarImage src={player.photo_url} alt={player.name} />
              <AvatarFallback className="bg-muted text-muted-foreground text-2xl">{getInitials(player.name)}</AvatarFallback>
            </Avatar>
            {canUploadPhoto && (
              <>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onPhotoUpload} />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors shadow-lg"
                  data-testid="upload-photo-btn"
                >
                  <Camera className="w-4 h-4" weight="fill" />
                </button>
              </>
            )}
          </div>
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-heading text-3xl font-bold" data-testid="player-name">{player.name}</h1>
              {isAdmin ? (
                <Select value={player.tier || 'challenger'} onValueChange={onTierChange}>
                  <SelectTrigger className="w-32 h-7 text-xs" data-testid="admin-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="premier">Premier</SelectItem>
                    <SelectItem value="challenger">Challenger</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Badge className={player.tier === 'premier' ? 'tier-premier' : 'tier-challenger'}>
                  {player.tier === 'premier' ? 'Premier' : 'Challenger'}
                </Badge>
              )}
            </div>
            {player.nickname && <p className="text-lg text-muted-foreground">"{player.nickname}"</p>}
            <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
              {player.age && <span>Age: {player.age}</span>}
              {player.playing_style && <span>Style: {player.playing_style}</span>}
              {player.cue_brand && <span>Cue: {player.cue_brand}</span>}
              {player.mobile_no && <span data-testid="player-mobile">Mobile: {player.mobile_no}</span>}
              {player.its_number && <span>ITS: {player.its_number}</span>}
              {player.claimed_by_email && (
                <span data-testid="player-claimed-email">
                  Email: <span className="font-mono text-xs">{player.claimed_by_email}</span>
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="text-center" data-testid="elo-rating-display">
              <div className={`text-3xl font-heading font-bold ${getEloColor(eloRating)}`}>{eloRating}</div>
              <p className={`text-xs font-semibold ${getEloColor(eloRating)}`}>{getEloLabel(eloRating)} ELO</p>
              {player.baseline_elo != null && (
                <EloBaselineLine
                  eloRating={eloRating}
                  baseline={player.baseline_elo}
                />
              )}
            </div>
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold ${
                player.ranking === 1 ? 'rank-1' : player.ranking === 2 ? 'rank-2' : player.ranking === 3 ? 'rank-3' : 'bg-muted text-muted-foreground'
              }`}>#{player.ranking}</div>
              <p className="text-xs text-muted-foreground mt-1">Club Rank</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-heading font-bold text-primary">{player.total_points}</p>
              <p className="text-xs text-muted-foreground">Points</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
